import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { FormGroup, Grid, Box, Button, FormRow, Spinner, Text } from '@cad-arc';
import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { ConsolidatedLocationView, EventPanelFormType } from '@mark43/cad-api';
import {
    FormLocationSelect,
    LocationFormRowWrapper,
} from '../../forms/integrated-form-controls/form-location-select';
import { useBroadcast } from '../../common/broadcast-channel/use-broadcast';
import { useEventMetadataStore } from './event-metadata';
import { FieldIdentifierProvider } from '../../forms/field-identifier';
import { useWorkspacePanel } from '../../panels/hooks/use-workspace-panel';
import { observer } from 'mobx-react-lite';
import { autorun } from 'mobx';
import { useSetActiveEventPanelTab } from '../active-event-panel-tab-provider';
import { FormTextarea } from '../../forms/integrated-form-controls/form-text-area';
import { FormInput } from '../../forms/integrated-form-controls/form-input';
import { useViewLocationOnMap } from '../../location/hooks/use-view-location-on-map';
import { ClassificationStrategy } from '../../common/types';
import { EsriMapEntityTypeEnum } from '../../esri/types';
import { isValidLatLng } from '../../location/helpers';
import { usePrevious } from '../../common/hooks/use-previous';
import { useShouldRefreshE911Calls } from './use-should-refresh-e911-calls';
import { useMaritimeFormat } from '../../common/hooks/use-maritime-format';
import { MaritimeFormatsRadioButtonsSection } from '../../common/components/maritime-formats-radio-buttons-section';
import {
    useAutoImportLocationOnE911,
    LOCATION_PREFILLED_MESSAGE,
    LOCATION_PREFILL_FAILED_MESSAGE,
} from '../tabs/ani-ali-e911/use-auto-import-location-on-e911';

function locationCanBeViewedOnMap(location: ConsolidatedLocationView | null): boolean {
    return !!(location && location.latitude && location.longitude && location.fullAddressKey);
}

const ViewOnMapButton = observer(function ViewOnMapButton(props: {
    eventId?: number;
    form: UseFormReturn<EventPanelFormType>;
}) {
    const eventMetadata = useEventMetadataStore().state.metadata;
    const isCleared = eventMetadata?.eventId && !eventMetadata.isActive;
    const { postMessage: legacyStoreBroadcastPostMessage } = useBroadcast('legacy-store-broadcast');
    const viewLocationOnMap = useViewLocationOnMap();
    const { disableLegacyMapAndSearches, mapWindowEnabled } = useEnabledFeatures();
    const shouldRefresh911Calls = useShouldRefreshE911Calls(props.form);

    const eventLocation = useWatch({
        control: props.form.control,
        name: 'location',
    }) as ConsolidatedLocationView | null;
    const showViewOnMapButton = props.eventId || locationCanBeViewedOnMap(eventLocation);
    return (
        <Grid gridColumn="span 2 / auto">
            {showViewOnMapButton ? (
                <Button
                    size="sm"
                    variant="ghost"
                    tabIndex={-1}
                    onClick={() => {
                        // For cleared events, legacy cad has an issue of not having the event in state, and so the preferred
                        // command SHOW_EVENT_ON_AVL_MAP will error. For this case we do the best we can by moving the the location
                        // on the map (which unfortunately wont be paired with extra event metadata in the ledger)
                        if (props.eventId && !isCleared) {
                            if (disableLegacyMapAndSearches || mapWindowEnabled) {
                                viewLocationOnMap({
                                    entityId: props.eventId!,
                                    entityType: EsriMapEntityTypeEnum.EVENT,
                                });
                            } else {
                                legacyStoreBroadcastPostMessage({
                                    type: 'SHOW_EVENT_ON_AVL_MAP',
                                    payload: { eventId: props.eventId!, location: eventLocation },
                                });
                            }
                        } else if (
                            eventLocation?.fullAddressKey &&
                            eventLocation.latitude !== undefined &&
                            eventLocation.longitude !== undefined
                        ) {
                            if (disableLegacyMapAndSearches || mapWindowEnabled) {
                                viewLocationOnMap({
                                    geoPointLocation: eventLocation,
                                    shouldRefresh911Calls,
                                });
                            } else {
                                legacyStoreBroadcastPostMessage({
                                    type: 'SHOW_LOCATION_ON_AVL_MAP',
                                    payload: {
                                        fullAddress: eventLocation.fullAddressKey,
                                        latitude: eventLocation.latitude,
                                        longitude: eventLocation.longitude,
                                    },
                                });
                            }
                        }
                    }}
                >
                    View on Map
                </Button>
            ) : undefined}
        </Grid>
    );
});

export const EventPanelFormLocationFields = observer(function EventPanelFormLocationFields(props: {
    eventId?: number;
    form: UseFormReturn<EventPanelFormType>;
    /** Derives the classification fields once the auto-import commits a location. */
    classifyEventLocation: () => void;
}): React.ReactElement {
    const eventForm = props.form;
    const autoImportLocationStatus = useAutoImportLocationOnE911(
        eventForm,
        props.classifyEventLocation
    );
    const {
        disableLegacyMapAndSearches,
        mapWindowEnabled,
        multiAgencySubdivisions,
        subpremiseSupport,
        focusMapDuringActiveEvent,
        focusMapDuringCallCreation,
        eventLocationNotes,
        maritimeCoordinatesEnabled,
    } = useEnabledFeatures();
    const eventMetadata = useEventMetadataStore().state.metadata;
    const { postMessage: legacyStoreBroadcastPostMessage } = useBroadcast('legacy-store-broadcast');

    const workspacePanel = useWorkspacePanel<'EVENT'>();
    const clientUuid = workspacePanel.uuid;
    const shouldAutoFocusLocationSelect =
        workspacePanel.getPanelState('shouldFocusEventPanelForm') ?? true;
    const viewLocationOnMap = useViewLocationOnMap();
    const prevEventId = usePrevious(props.eventId);
    const shouldRefresh911Calls = useShouldRefreshE911Calls(eventForm);
    const [locationCoordinates, setLocationCoordinates] = useState<{
        latitude?: number;
        longitude?: number;
    }>(() => {
        const locations = eventForm.getValues('location') as ConsolidatedLocationView;

        return {
            latitude: locations?.latitude,
            longitude: locations?.longitude,
        };
    });
    const { formattedValue, maritimeFormat, setMaritimeFormat } = useMaritimeFormat({
        latitude: locationCoordinates?.latitude,
        longitude: locationCoordinates?.longitude,
    });

    useEffect(() => {
        return autorun(() => {
            const panelIsFocused = workspacePanel.isFocused;
            const eventLocation = eventForm.getValues().location as ConsolidatedLocationView | null;
            if (
                panelIsFocused &&
                eventMetadata?.isActive &&
                focusMapDuringActiveEvent &&
                props.eventId &&
                eventLocation
            ) {
                if ((disableLegacyMapAndSearches || mapWindowEnabled) && props.eventId) {
                    // for newly created event, the event data is available in event window from the 'form' object.
                    // However map window have to fetch the new event data after the nudge is received.
                    // Most likely viewLocationOnMap will be completed BEFORE new event data is ready in map window.
                    // send latitude/longitude as fallback if the entity is not available yet,
                    // so map can move to given lat/lng and open map ledger with loading text. Once map window fetched new event data,
                    // map ledger will rerender with completed data.
                    if (prevEventId === undefined) {
                        setTimeout(
                            () =>
                                viewLocationOnMap({
                                    entityId: props.eventId as number,
                                    entityType: EsriMapEntityTypeEnum.EVENT,
                                    latitude: eventLocation.latitude,
                                    longitude: eventLocation.longitude,
                                    doNotFocus: true,
                                }),
                            // delay 1000ms here for new event will not guarantee map window has new data ready, but will reduce the chance to show a loading event map ledger.
                            1000
                        );
                    } else {
                        viewLocationOnMap({
                            entityId: props.eventId,
                            entityType: EsriMapEntityTypeEnum.EVENT,
                            doNotFocus: true,
                        });
                    }
                } else {
                    legacyStoreBroadcastPostMessage({
                        type: 'SHOW_EVENT_ON_AVL_MAP_IN_BACKGROUND',
                        payload: {
                            eventId: props.eventId,
                            location: {
                                ...eventLocation,
                                multiAgencyClassificationDtos: cloneDeep(
                                    eventLocation.multiAgencyClassificationDtos
                                ),
                            },
                        },
                    });
                }
            }
        });
    }, [
        focusMapDuringActiveEvent,
        eventForm,
        eventMetadata?.isActive,
        disableLegacyMapAndSearches,
        mapWindowEnabled,
        viewLocationOnMap,
        legacyStoreBroadcastPostMessage,
        props.eventId,
        workspacePanel,
        prevEventId,
    ]);

    useEffect(() => {
        const subscription = eventForm.watch((data, { type, name }) => {
            const location = data.location as ConsolidatedLocationView | null;

            if (maritimeCoordinatesEnabled) {
                setLocationCoordinates({
                    latitude: location?.latitude,
                    longitude: location?.longitude,
                });
            }
            if (
                !eventMetadata?.eventId &&
                type === 'change' &&
                name === 'location' &&
                focusMapDuringCallCreation &&
                location &&
                location.fullAddressKey &&
                isValidLatLng(location.latitude, location.longitude)
            ) {
                if (disableLegacyMapAndSearches || mapWindowEnabled) {
                    viewLocationOnMap({
                        geoPointLocation: cloneDeep(location),
                        shouldRefresh911Calls,
                        doNotFocus: true,
                    });
                } else {
                    legacyStoreBroadcastPostMessage({
                        type: 'SHOW_LOCATION_ON_AVL_MAP_IN_BACKGROUND',
                        payload: {
                            fullAddress: location.fullAddressKey,
                            latitude: location.latitude!,
                            longitude: location.longitude!,
                        },
                    });
                }
            }
        });
        return () => {
            subscription.unsubscribe();
        };
    }, [
        focusMapDuringCallCreation,
        eventForm,
        eventMetadata?.eventId,
        disableLegacyMapAndSearches,
        mapWindowEnabled,
        legacyStoreBroadcastPostMessage,
        viewLocationOnMap,
        shouldRefresh911Calls,
        maritimeCoordinatesEnabled,
    ]);

    const setActiveEventPanelTab = useSetActiveEventPanelTab();

    const handleClickCautionMarker = useCallback(() => {
        setActiveEventPanelTab('CAUTIONS');
    }, [setActiveEventPanelTab]);

    const handleBlur = useCallback(() => {
        const location = eventForm.getValues('location') as ConsolidatedLocationView | null;
        const isVerifiedLocation = !!location?.areCoordinatesVerified;
        if (!props.eventId && isVerifiedLocation) {
            setActiveEventPanelTab('MERGED_EVENTS');
        }
    }, [eventForm, props.eventId, setActiveEventPanelTab]);

    const isNewEvent = props.eventId === undefined;
    return (
        <FormGroup spacing={1}>
            <LocationFormRowWrapper subpremisesEnabled={subpremiseSupport}>
                <FieldIdentifierProvider value={`${clientUuid}:EVENT_LOCATION`}>
                    <FormLocationSelect
                        onBlur={handleBlur}
                        onClickCautionMarker={handleClickCautionMarker}
                        control={eventForm.control}
                        label="Location"
                        name="location"
                        length="lg"
                        showRequiredIndicator={true}
                        autoFocus={shouldAutoFocusLocationSelect}
                        asEditable={props.eventId ? !subpremiseSupport : false}
                        placeInputOutside={subpremiseSupport ? isNewEvent : !isNewEvent}
                        highestPriorityLocationCaution={
                            eventMetadata?.highestPriorityLocationCaution
                        }
                        isNearbyLocationCautionPriority1={
                            eventMetadata?.isNearbyLocationCautionPriority1
                        }
                        classificationStrategy={
                            multiAgencySubdivisions
                                ? ClassificationStrategy.MULTI_AGENCY
                                : ClassificationStrategy.SINGLE_AGENCY
                        }
                        eventNotificationName="EVENT_LOCATION"
                    />
                </FieldIdentifierProvider>
                {!subpremiseSupport && (
                    <FormInput
                        control={eventForm.control}
                        label="Apt, Unit"
                        length="sm"
                        name="locationSubPremise"
                        eventNotificationName="EVENT_LOCATION_SUB_PREMISE"
                        asEditable={!!props.eventId}
                    />
                )}
            </LocationFormRowWrapper>
            {autoImportLocationStatus === 'resolving' && <Spinner size="sm" />}
            {autoImportLocationStatus === 'success' && (
                <Text isItalic fontSize="md">
                    {LOCATION_PREFILLED_MESSAGE}
                </Text>
            )}
            {autoImportLocationStatus === 'failed' && (
                <Text isItalic fontSize="md" color="negative">
                    {LOCATION_PREFILL_FAILED_MESSAGE}
                </Text>
            )}
            {maritimeCoordinatesEnabled &&
                isValidLatLng(locationCoordinates?.latitude, locationCoordinates?.longitude) && (
                    <MaritimeFormatsRadioButtonsSection
                        formattedValue={formattedValue}
                        activeFormat={maritimeFormat}
                        setActiveFormat={setMaritimeFormat}
                    />
                )}
            <FormRow>
                {eventLocationNotes ? (
                    <FormTextarea
                        length="lg"
                        control={eventForm.control}
                        label="Location Notes"
                        name="locationNotes"
                        asEditable={true}
                        eventNotificationName="EVENT_LOCATION_NOTES"
                    />
                ) : undefined}
                {subpremiseSupport ? (
                    <Box position="absolute" right={4}>
                        <ViewOnMapButton eventId={props.eventId} form={eventForm} />
                    </Box>
                ) : (
                    <ViewOnMapButton eventId={props.eventId} form={eventForm} />
                )}
            </FormRow>
        </FormGroup>
    );
});
